/**
 * Pure diff util for the PQR editable preview's Save & Generate confirmation
 * dialog. Compares two `PqrEditableSnapshot` values (the baseline loaded
 * from the server vs the user's current edits) and emits a grouped, human-
 * readable change list ready to render in the dialog.
 *
 * The same util powers the toolbar's "N unsaved changes" badge by counting
 * the rows across all groups.
 */

import { weldingVariableComposedFieldMap, type WeldingVariableRowKey } from './pqrWeldingVariableRowDefs'

const WELDING_VARIABLE_COMPOSED_FIELDS = weldingVariableComposedFieldMap()

export interface PqrEditableSignatory {
  id: string
  name: string
  title?: string
  date?: string
  signature?: string
  stampImage?: string
  signedAt?: string
  memberId?: string
}

/**
 * One row of the editable Materials table. Mirrors the row shape rendered by
 * the PQR preview and the PDF (one row per pipe). Index keys are preserved
 * so the diff can produce a sparse server patch.
 */
export interface PqrEditableMaterialRow {
  lotIndex: number
  pipeIndex: number
  /** Lot-level field — first-of-array on `manufacturerLots[lotIndex].materialSpec`. */
  materialSpec: string
  /** Lot-level field — first-of-array on `manufacturerLots[lotIndex].typeGrade`. */
  typeGrade: string
  /** Lot-level field — first-of-array on `manufacturerLots[lotIndex].pipeManufacturer`. */
  pipeManufacturer: string
  /** Per-pipe field — `manufacturerLots[lotIndex].pipes[pipeIndex].heatNumber`. */
  heatNumber: string
  /** Display label (e.g. "Pipe 2") — used by the diff dialog. */
  label: string
}

export type JointParameterKey =
  | 'parameter1'
  | 'parameter2'
  | 'parameter3'
  | 'parameter4'
  | 'parameter5'
  | 'parameter6'
  | 'parameter7'
  | 'parameter8'
  | 'parameter9'
  | 'parameter10'
  | 'parameter11'
  | 'parameter12'
  | 'parameter13'

export const JOINT_PARAMETER_KEYS: JointParameterKey[] = [
  'parameter1',
  'parameter2',
  'parameter3',
  'parameter4',
  'parameter5',
  'parameter6',
  'parameter7',
  'parameter8',
  'parameter9',
  'parameter10',
  'parameter11',
  'parameter12',
  'parameter13'
]

/**
 * Editable Joint Preparation block. Targets exactly one profile inside
 * `sections.bevel.bevels[bevelIndex].geometryParameterProfiles[profileIndex]`.
 * The PDF only renders the first profile of the first bevel, so the preview
 * fixes `bevelIndex = profileIndex = 0` in slice 03.
 */
export interface PqrEditableJointPreparation {
  bevelIndex: number
  profileIndex: number
  /** Joint type display string (read-only from the PQR perspective). */
  jointType: string
  /** All 13 slots present — blank slots render as empty inputs. */
  parameters: Record<JointParameterKey, string>
}

/**
 * One cell of the editable Welding Variables matrix. `value` is what the user
 * sees and edits (composed for Gas Flow Rate, raw otherwise); `min`/`max`
 * are populated only for the Gas Flow Rate row so the diff/patch builders
 * can persist the two underlying fields independently.
 */
export interface PqrEditableWeldingVariableCellSnapshot {
  value: string
  min?: string
  max?: string
}

export interface PqrEditableWeldingVariableRowSnapshot {
  rowKey: WeldingVariableRowKey
  label: string
  unit?: string
  values: PqrEditableWeldingVariableCellSnapshot[]
}

export interface PqrEditableWeldingVariablesSnapshot {
  bevelIndex: number
  passes: string[]
  rows: PqrEditableWeldingVariableRowSnapshot[]
}

export type WeldingParameterColumnKey =
  | 'preheat'
  | 'amperage'
  | 'voltage'
  | 'wfs'
  | 'travel'
  | 'heatInput'

export const WELDING_PARAMETER_COLUMN_LABELS: Record<WeldingParameterColumnKey, string> = {
  preheat: 'Preheat / Interpass',
  amperage: 'Amperage',
  voltage: 'Voltage',
  wfs: 'Wire Feed Speed',
  travel: 'Travel Speed',
  heatInput: 'Heat Input'
}

/**
 * One cell of the Welding Parameters override matrix. `aggregate` is the
 * live value from per-weld telemetry (informational only); `override` is the
 * value persisted at `sections.pqr.overrides.weldingParameters` — `null`
 * means "use aggregate".
 */
export interface PqrEditableWeldingParametersCell {
  aggregate: { min: string; max: string }
  override: { min: string; max: string } | null
}

export interface PqrEditableWeldingParametersRow {
  passLabel: string
  cells: Record<WeldingParameterColumnKey, PqrEditableWeldingParametersCell>
}

export interface PqrEditableWeldingParametersSnapshot {
  passes: string[]
  rows: PqrEditableWeldingParametersRow[]
}

export interface PqrEditableNotesWelderRow {
  role: string
  name: string
}

export interface PqrEditableNotesEquipmentRow {
  label: string
  value: string
}

/**
 * Editable notes-overrides snapshot. `isOverride` reflects whether the
 * server has a persisted override for this list right now — used to drive
 * the "Overridden …" badge and the "Reset to aggregated" affordance.
 */
export interface PqrEditableNotesSnapshot {
  welders: { isOverride: boolean; rows: PqrEditableNotesWelderRow[] }
  equipment: { isOverride: boolean; rows: PqrEditableNotesEquipmentRow[] }
}

export interface PqrEditableSnapshot {
  pqrNumber: string
  revision: string
  preparedBy: PqrEditableSignatory
  reviewedBy: PqrEditableSignatory
  comments: string
  /** Materials rows. Optional for backward compat with slice-01-only callers. */
  materials?: PqrEditableMaterialRow[]
  /** Joint Preparation editable block. Optional for backward compat. */
  jointPreparation?: PqrEditableJointPreparation
  /** Welding Variables matrix. Optional for backward compat. */
  weldingVariables?: PqrEditableWeldingVariablesSnapshot
  /** Welding Parameters overrides matrix. Optional for backward compat. */
  weldingParameters?: PqrEditableWeldingParametersSnapshot
  /** Notes (Welders + Equipment) overrides. Optional for backward compat. */
  notes?: PqrEditableNotesSnapshot
}

export type PqrDiffGroupName =
  | 'Identifiers'
  | 'Signatures'
  | 'Materials'
  | 'Joint Preparation'
  | 'Welding Variables'
  | 'Welding Parameters (override)'
  | 'Notes (override)'
  | 'Comments'

export interface PqrDiffRow {
  field: string
  before: string
  after: string
}

export interface PqrDiffGroup {
  group: PqrDiffGroupName
  rows: PqrDiffRow[]
}

export interface PqrPreviewDiff {
  groups: PqrDiffGroup[]
  hasChanges: boolean
}

const EMPTY_PLACEHOLDER = '(empty)'

function formatString(value: string): string {
  const trimmed = (value ?? '').trim()
  return trimmed.length > 0 ? trimmed : EMPTY_PLACEHOLDER
}

function formatSignatory(person: PqrEditableSignatory): string {
  const name = (person?.name ?? '').trim()
  const id = (person?.id ?? '').trim()
  const title = (person?.title ?? '').trim()
  const date = (person?.date ?? '').trim()
  const sigState = person?.signature?.trim()
    ? 'signed'
    : 'unsigned'
  if (!name) return EMPTY_PLACEHOLDER
  const base = id ? `${name} (${id})` : name
  const extras = [title, date, sigState !== 'unsigned' ? `signature: ${sigState}` : '']
    .filter(Boolean)
    .join(', ')
  return extras ? `${base} — ${extras}` : base
}

function stringDiffers(a: string, b: string): boolean {
  return (a ?? '').trim() !== (b ?? '').trim()
}

function signatoryDiffers(a: PqrEditableSignatory, b: PqrEditableSignatory): boolean {
  return (
    (a?.id ?? '').trim() !== (b?.id ?? '').trim() ||
    (a?.name ?? '').trim() !== (b?.name ?? '').trim() ||
    (a?.title ?? '').trim() !== (b?.title ?? '').trim() ||
    (a?.date ?? '').trim() !== (b?.date ?? '').trim() ||
    Boolean(a?.signature?.trim()) !== Boolean(b?.signature?.trim()) ||
    Boolean(a?.stampImage?.trim()) !== Boolean(b?.stampImage?.trim())
  )
}

export function buildPqrPreviewDiff(
  initial: PqrEditableSnapshot,
  current: PqrEditableSnapshot
): PqrPreviewDiff {
  const identifierRows: PqrDiffRow[] = []
  if (stringDiffers(initial.pqrNumber, current.pqrNumber)) {
    identifierRows.push({
      field: 'PQR No.',
      before: formatString(initial.pqrNumber),
      after: formatString(current.pqrNumber)
    })
  }
  if (stringDiffers(initial.revision, current.revision)) {
    identifierRows.push({
      field: 'Rev.',
      before: formatString(initial.revision),
      after: formatString(current.revision)
    })
  }

  const signatureRows = buildSignatureDiffRows(initial, current)

  // Comments diff intentionally dropped — Slice 03 of
  // pqr-preview-field-navigation sources comments from FloorSheet
  // `operators.notes` so there is no PQR-level edit to diff.
  // Materials diff intentionally dropped — Slice 05 of
  // pqr-preview-field-navigation: per-pipe rows navigate to the
  // WpsBaseMaterial page. No PQR-level edit to diff.
  const materialsRows: PqrDiffRow[] = []
  // Joint Preparation diff intentionally dropped — Slice 06 of
  // pqr-preview-field-navigation: clicking the panel navigates to
  // WpsBevelDetails, edits happen in place. No PQR-level edit to diff.
  const jointRows: PqrDiffRow[] = []
  // Welding Variables diff intentionally dropped — Slice 07 of
  // pqr-preview-field-navigation: per-cell clicks navigate to
  // WpsWeldDetails. No PQR-level edit to diff.
  const weldVarRows: PqrDiffRow[] = []
  // Welding Parameters diff intentionally dropped — Slice 08 of
  // pqr-preview-field-navigation: per-cell clicks navigate to the
  // per-weld WeldParameters page. No PQR-level edit to diff.
  const weldParamRows: PqrDiffRow[] = []
  // Notes diff intentionally dropped — Slice 04 of
  // pqr-preview-field-navigation: equipment edits flow through the
  // project edit page; welder edits through the FloorSheet operators
  // tab. There is no PQR-level edit to diff.
  const notesRows: PqrDiffRow[] = []

  const groups: PqrDiffGroup[] = []
  if (identifierRows.length > 0) groups.push({ group: 'Identifiers', rows: identifierRows })
  if (signatureRows.length > 0) groups.push({ group: 'Signatures', rows: signatureRows })
  if (materialsRows.length > 0) groups.push({ group: 'Materials', rows: materialsRows })
  if (jointRows.length > 0) groups.push({ group: 'Joint Preparation', rows: jointRows })
  if (weldVarRows.length > 0) groups.push({ group: 'Welding Variables', rows: weldVarRows })
  if (weldParamRows.length > 0)
    groups.push({ group: 'Welding Parameters (override)', rows: weldParamRows })
  if (notesRows.length > 0) groups.push({ group: 'Notes (override)', rows: notesRows })

  return {
    groups,
    hasChanges: groups.length > 0
  }
}

function buildSignatureDiffRows(
  initial: PqrEditableSnapshot,
  current: PqrEditableSnapshot
): PqrDiffRow[] {
  const signatureRows: PqrDiffRow[] = []
  if (signatoryDiffers(initial.preparedBy, current.preparedBy)) {
    signatureRows.push({
      field: 'Prepared By',
      before: formatSignatory(initial.preparedBy),
      after: formatSignatory(current.preparedBy)
    })
  }
  if (signatoryDiffers(initial.reviewedBy, current.reviewedBy)) {
    signatureRows.push({
      field: 'Reviewed By',
      before: formatSignatory(initial.reviewedBy),
      after: formatSignatory(current.reviewedBy)
    })
  }
  return signatureRows
}

/** Diff for confirm / discard UI — omits PQR No. and Rev. identifier rows. */
export function buildPqrPreviewSignificantDiff(
  initial: PqrEditableSnapshot,
  current: PqrEditableSnapshot
): PqrPreviewDiff {
  const groups: PqrDiffGroup[] = []
  const signatureRows = buildSignatureDiffRows(initial, current)
  if (signatureRows.length > 0) groups.push({ group: 'Signatures', rows: signatureRows })
  return {
    groups,
    hasChanges: groups.length > 0
  }
}

export function hasSignificantPqrPreviewChanges(
  initial: PqrEditableSnapshot,
  current: PqrEditableSnapshot
): boolean {
  return buildPqrPreviewSignificantDiff(initial, current).hasChanges
}

const MATERIAL_FIELD_LABELS: Record<
  'materialSpec' | 'typeGrade' | 'pipeManufacturer' | 'heatNumber',
  string
> = {
  materialSpec: 'Material Spec',
  typeGrade: 'Type / Grade',
  pipeManufacturer: 'Manufacturer',
  heatNumber: 'Heat No.'
}

function buildMaterialsDiffRows(
  initial: PqrEditableMaterialRow[],
  current: PqrEditableMaterialRow[]
): PqrDiffRow[] {
  const rows: PqrDiffRow[] = []
  const byKey = new Map<string, PqrEditableMaterialRow>()
  initial.forEach((r) => byKey.set(`${r.lotIndex}:${r.pipeIndex}`, r))

  current.forEach((cur) => {
    const prev = byKey.get(`${cur.lotIndex}:${cur.pipeIndex}`)
    if (!prev) return
    ;(Object.keys(MATERIAL_FIELD_LABELS) as Array<keyof typeof MATERIAL_FIELD_LABELS>).forEach(
      (field) => {
        if (stringDiffers(prev[field], cur[field])) {
          rows.push({
            field: `${cur.label} — ${MATERIAL_FIELD_LABELS[field]}`,
            before: formatString(prev[field]),
            after: formatString(cur[field])
          })
        }
      }
    )
  })
  return rows
}

function buildJointPreparationDiffRows(
  initial: PqrEditableJointPreparation | undefined,
  current: PqrEditableJointPreparation | undefined
): PqrDiffRow[] {
  if (!initial || !current) return []
  if (initial.bevelIndex !== current.bevelIndex || initial.profileIndex !== current.profileIndex) {
    // The PQR preview never lets the user swap targeted bevel/profile in
    // slice 03 — if it ever happens we treat the snapshot as incomparable.
    return []
  }
  const rows: PqrDiffRow[] = []
  JOINT_PARAMETER_KEYS.forEach((key) => {
    const before = initial.parameters[key] ?? ''
    const after = current.parameters[key] ?? ''
    if (stringDiffers(before, after)) {
      const n = key.slice('parameter'.length)
      rows.push({
        field: `Parameter ${n}`,
        before: formatString(before),
        after: formatString(after)
      })
    }
  })
  return rows
}

/**
 * Build the sparse `jointPreparation` patch payload for the save request.
 * Returns `undefined` when no parameters changed so the caller can omit the
 * key entirely instead of sending an empty object.
 */
export function buildJointPreparationServerPatch(
  initial: PqrEditableJointPreparation | undefined,
  current: PqrEditableJointPreparation | undefined
): {
  bevelIndex: number
  profileIndex: number
  parameters: Partial<Record<JointParameterKey, string>>
} | undefined {
  if (!initial || !current) return undefined
  if (initial.bevelIndex !== current.bevelIndex || initial.profileIndex !== current.profileIndex) {
    return undefined
  }
  const parameters: Partial<Record<JointParameterKey, string>> = {}
  let touched = false
  JOINT_PARAMETER_KEYS.forEach((key) => {
    const before = initial.parameters[key] ?? ''
    const after = current.parameters[key] ?? ''
    if (stringDiffers(before, after)) {
      parameters[key] = after.trim()
      touched = true
    }
  })
  if (!touched) return undefined
  return {
    bevelIndex: current.bevelIndex,
    profileIndex: current.profileIndex,
    parameters
  }
}

/**
 * Welding-variables diff: one row per `(passLabel, label)` cell that changed.
 * Skipped entirely when either side is missing or the bevel/pass shape has
 * drifted between the snapshots (the PQR preview never edits those, so the
 * common case is "they match").
 */
function buildWeldingVariablesDiffRows(
  initial: PqrEditableWeldingVariablesSnapshot | undefined,
  current: PqrEditableWeldingVariablesSnapshot | undefined
): PqrDiffRow[] {
  if (!initial || !current) return []
  if (
    initial.bevelIndex !== current.bevelIndex ||
    initial.passes.length !== current.passes.length ||
    initial.passes.some((p, i) => p !== current.passes[i])
  ) {
    return []
  }
  const rows: PqrDiffRow[] = []
  current.rows.forEach((curRow) => {
    const prevRow = initial.rows.find((r) => r.rowKey === curRow.rowKey)
    if (!prevRow) return
    curRow.values.forEach((curCell, passIdx) => {
      const prevCell = prevRow.values[passIdx]
      if (!prevCell) return
      const composedFields = WELDING_VARIABLE_COMPOSED_FIELDS.get(curRow.rowKey)
      if (composedFields) {
        if (
          stringDiffers(prevCell.min ?? '', curCell.min ?? '') ||
          stringDiffers(prevCell.max ?? '', curCell.max ?? '')
        ) {
          rows.push({
            field: `${current.passes[passIdx]} — ${curRow.label}`,
            before: formatString(prevCell.value),
            after: formatString(curCell.value)
          })
        }
        return
      }
      if (stringDiffers(prevCell.value, curCell.value)) {
        rows.push({
          field: `${current.passes[passIdx]} — ${curRow.label}`,
          before: formatString(prevCell.value),
          after: formatString(curCell.value)
        })
      }
    })
  })
  return rows
}

/**
 * Build the sparse `weldingVariables` patch payload sent on the save request.
 * Groups changed cells by `passLabel` so the server can resolve each entry to
 * a single `weldDetails.groups[key]` mutation. Composed min/max rows split
 * into their underlying field keys (e.g. `gasFlowRateMin` / `gasFlowRateMax`).
 */
export function buildWeldingVariablesServerPatch(
  initial: PqrEditableWeldingVariablesSnapshot | undefined,
  current: PqrEditableWeldingVariablesSnapshot | undefined
): Array<{ passLabel: string; bevelIndex: number; fields: Record<string, string> }> | undefined {
  if (!initial || !current) return undefined
  if (
    initial.bevelIndex !== current.bevelIndex ||
    initial.passes.length !== current.passes.length ||
    initial.passes.some((p, i) => p !== current.passes[i])
  ) {
    return undefined
  }

  const perPass = new Map<string, Record<string, string>>()
  current.rows.forEach((curRow) => {
    const prevRow = initial.rows.find((r) => r.rowKey === curRow.rowKey)
    if (!prevRow) return
    curRow.values.forEach((curCell, passIdx) => {
      const prevCell = prevRow.values[passIdx]
      if (!prevCell) return
      const passLabel = current.passes[passIdx]
      const composedFields = WELDING_VARIABLE_COMPOSED_FIELDS.get(curRow.rowKey)
      if (composedFields) {
        const [minField, maxField] = composedFields
        const minChanged = stringDiffers(prevCell.min ?? '', curCell.min ?? '')
        const maxChanged = stringDiffers(prevCell.max ?? '', curCell.max ?? '')
        if (!minChanged && !maxChanged) return
        const bucket = perPass.get(passLabel) ?? {}
        if (minChanged) bucket[minField] = (curCell.min ?? '').trim()
        if (maxChanged) bucket[maxField] = (curCell.max ?? '').trim()
        perPass.set(passLabel, bucket)
        return
      }
      if (!stringDiffers(prevCell.value, curCell.value)) return
      const def = curRow.rowKey
      const bucket = perPass.get(passLabel) ?? {}
      // `def` is the rowKey, which (for non-composed rows) doubles as the field key.
      bucket[def] = (curCell.value ?? '').trim()
      perPass.set(passLabel, bucket)
    })
  })

  if (perPass.size === 0) return undefined
  const out: Array<{ passLabel: string; bevelIndex: number; fields: Record<string, string> }> = []
  perPass.forEach((fields, passLabel) => {
    out.push({ passLabel, bevelIndex: current.bevelIndex, fields })
  })
  return out
}

// ---------------------------------------------------------------------------
// Welding Parameters overrides diff + patch (slice 05)
// ---------------------------------------------------------------------------

function formatCellPair(min: string, max: string): string {
  const mn = (min ?? '').trim()
  const mx = (max ?? '').trim()
  if (!mn && !mx) return EMPTY_PLACEHOLDER
  if (mn === mx) return mn || mx
  if (!mn) return mx
  if (!mx) return mn
  return `${mn}–${mx}`
}

function cellOverrideKey(o: { min: string; max: string } | null): string {
  if (!o) return ''
  return `${(o.min ?? '').trim()}|${(o.max ?? '').trim()}`
}

const WELDING_PARAMETER_COLUMN_ORDER: WeldingParameterColumnKey[] = [
  'preheat',
  'amperage',
  'voltage',
  'wfs',
  'travel',
  'heatInput'
]

function buildWeldingParametersDiffRows(
  initial: PqrEditableWeldingParametersSnapshot | undefined,
  current: PqrEditableWeldingParametersSnapshot | undefined
): PqrDiffRow[] {
  if (!initial || !current) return []
  if (
    initial.passes.length !== current.passes.length ||
    initial.passes.some((p, i) => p !== current.passes[i])
  ) {
    return []
  }
  const rows: PqrDiffRow[] = []
  current.rows.forEach((curRow) => {
    const prevRow = initial.rows.find((r) => r.passLabel === curRow.passLabel)
    if (!prevRow) return
    WELDING_PARAMETER_COLUMN_ORDER.forEach((colKey) => {
      const prevCell = prevRow.cells[colKey]
      const curCell = curRow.cells[colKey]
      if (!prevCell || !curCell) return
      if (cellOverrideKey(prevCell.override) === cellOverrideKey(curCell.override)) return
      const beforeStr = prevCell.override
        ? formatCellPair(prevCell.override.min, prevCell.override.max)
        : `(aggregate ${formatCellPair(prevCell.aggregate.min, prevCell.aggregate.max)})`
      const afterStr = curCell.override
        ? formatCellPair(curCell.override.min, curCell.override.max)
        : `(aggregate ${formatCellPair(curCell.aggregate.min, curCell.aggregate.max)})`
      rows.push({
        field: `${curRow.passLabel} — ${WELDING_PARAMETER_COLUMN_LABELS[colKey]}`,
        before: beforeStr,
        after: afterStr
      })
    })
  })
  return rows
}

/**
 * Build the full Welding Parameters override array sent on the save request.
 * The server treats the supplied array as a complete replacement of
 * `sections.pqr.overrides.weldingParameters` — so this collects every cell
 * that currently has an override (not just the ones the user just edited)
 * and groups by passLabel.
 *
 * Returns `undefined` when the overrides snapshot itself hasn't changed
 * (passes / number of overridden cells / values are all identical to the
 * baseline), so the caller can omit the key from the request entirely.
 */
export function buildWeldingParametersServerPatch(
  initial: PqrEditableWeldingParametersSnapshot | undefined,
  current: PqrEditableWeldingParametersSnapshot | undefined
):
  | Array<{
      passLabel: string
      cells: Partial<Record<WeldingParameterColumnKey, { min: string; max: string }>>
    }>
  | undefined {
  if (!current) return undefined

  // Detect "no change" — short-circuit so the request stays clean.
  const sameShape =
    !!initial &&
    initial.passes.length === current.passes.length &&
    initial.passes.every((p, i) => p === current.passes[i])
  if (sameShape) {
    let anyDelta = false
    for (const curRow of current.rows) {
      const prevRow = initial!.rows.find((r) => r.passLabel === curRow.passLabel)
      if (!prevRow) {
        anyDelta = true
        break
      }
      for (const col of WELDING_PARAMETER_COLUMN_ORDER) {
        if (
          cellOverrideKey(prevRow.cells[col].override) !==
          cellOverrideKey(curRow.cells[col].override)
        ) {
          anyDelta = true
          break
        }
      }
      if (anyDelta) break
    }
    if (!anyDelta) return undefined
  }

  const out: Array<{
    passLabel: string
    cells: Partial<Record<WeldingParameterColumnKey, { min: string; max: string }>>
  }> = []
  current.rows.forEach((row) => {
    const cells: Partial<Record<WeldingParameterColumnKey, { min: string; max: string }>> = {}
    WELDING_PARAMETER_COLUMN_ORDER.forEach((colKey) => {
      const override = row.cells[colKey]?.override
      if (!override) return
      cells[colKey] = {
        min: (override.min ?? '').trim(),
        max: (override.max ?? '').trim()
      }
    })
    if (Object.keys(cells).length > 0) {
      out.push({ passLabel: row.passLabel, cells })
    }
  })
  return out
}

// ---------------------------------------------------------------------------
// Notes overrides diff + patch (slice 06)
// ---------------------------------------------------------------------------

function welderRowsEqual(
  a: PqrEditableNotesWelderRow[],
  b: PqrEditableNotesWelderRow[]
): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if ((a[i].role ?? '').trim() !== (b[i].role ?? '').trim()) return false
    if ((a[i].name ?? '').trim() !== (b[i].name ?? '').trim()) return false
  }
  return true
}

function equipmentRowsEqual(
  a: PqrEditableNotesEquipmentRow[],
  b: PqrEditableNotesEquipmentRow[]
): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if ((a[i].label ?? '').trim() !== (b[i].label ?? '').trim()) return false
    if ((a[i].value ?? '').trim() !== (b[i].value ?? '').trim()) return false
  }
  return true
}

function buildNotesDiffRows(
  initial: PqrEditableNotesSnapshot | undefined,
  current: PqrEditableNotesSnapshot | undefined
): PqrDiffRow[] {
  if (!current) return []
  const init = initial ?? {
    welders: { isOverride: false, rows: [] },
    equipment: { isOverride: false, rows: [] }
  }
  const rows: PqrDiffRow[] = []

  if (init.welders.isOverride !== current.welders.isOverride) {
    rows.push({
      field: 'Notes Welders',
      before: init.welders.isOverride
        ? `${init.welders.rows.length} overridden row(s)`
        : '(aggregated)',
      after: current.welders.isOverride
        ? `${current.welders.rows.length} overridden row(s)`
        : '(aggregated)'
    })
  } else if (
    init.welders.isOverride &&
    !welderRowsEqual(init.welders.rows, current.welders.rows)
  ) {
    rows.push({
      field: 'Notes Welders',
      before: `${init.welders.rows.length} overridden row(s)`,
      after: `${current.welders.rows.length} overridden row(s)`
    })
  }

  if (init.equipment.isOverride !== current.equipment.isOverride) {
    rows.push({
      field: 'Notes Equipment',
      before: init.equipment.isOverride
        ? `${init.equipment.rows.length} overridden row(s)`
        : '(aggregated)',
      after: current.equipment.isOverride
        ? `${current.equipment.rows.length} overridden row(s)`
        : '(aggregated)'
    })
  } else if (
    init.equipment.isOverride &&
    !equipmentRowsEqual(init.equipment.rows, current.equipment.rows)
  ) {
    rows.push({
      field: 'Notes Equipment',
      before: `${init.equipment.rows.length} overridden row(s)`,
      after: `${current.equipment.rows.length} overridden row(s)`
    })
  }
  return rows
}

/**
 * Build the sparse `notesOverrides` patch sent on the save request. Tri-state
 * per sub-key: `undefined` (omit) when nothing changed for that list,
 * `null` when the user reset the override, an array (incl. empty) when the
 * user is installing/updating an override. Returns `undefined` when both
 * sub-keys are `undefined`.
 */
export function buildNotesOverridesServerPatch(
  initial: PqrEditableNotesSnapshot | undefined,
  current: PqrEditableNotesSnapshot | undefined
):
  | {
      welders?: Array<{ role: string; name: string }> | null
      equipment?: Array<{ label: string; value: string }> | null
    }
  | undefined {
  if (!current) return undefined
  const init = initial ?? {
    welders: { isOverride: false, rows: [] },
    equipment: { isOverride: false, rows: [] }
  }

  const out: {
    welders?: Array<{ role: string; name: string }> | null
    equipment?: Array<{ label: string; value: string }> | null
  } = {}

  if (init.welders.isOverride && !current.welders.isOverride) {
    out.welders = null
  } else if (current.welders.isOverride) {
    if (
      !init.welders.isOverride ||
      !welderRowsEqual(init.welders.rows, current.welders.rows)
    ) {
      out.welders = current.welders.rows.map((r) => ({
        role: (r.role ?? '').trim(),
        name: (r.name ?? '').trim()
      }))
    }
  }

  if (init.equipment.isOverride && !current.equipment.isOverride) {
    out.equipment = null
  } else if (current.equipment.isOverride) {
    if (
      !init.equipment.isOverride ||
      !equipmentRowsEqual(init.equipment.rows, current.equipment.rows)
    ) {
      out.equipment = current.equipment.rows.map((r) => ({
        label: (r.label ?? '').trim(),
        value: (r.value ?? '').trim()
      }))
    }
  }

  if (
    !Object.prototype.hasOwnProperty.call(out, 'welders') &&
    !Object.prototype.hasOwnProperty.call(out, 'equipment')
  ) {
    return undefined
  }
  return out
}

/**
 * Build the sparse `materials` patch sent on the save request. One entry per
 * (lotIndex, pipeIndex) row that has at least one changed cell, with only the
 * changed scalar fields populated. Rows without changes are omitted entirely.
 */
export function buildMaterialsServerPatch(
  initial: PqrEditableMaterialRow[],
  current: PqrEditableMaterialRow[]
): Array<{
  lotIndex: number
  pipeIndex: number
  materialSpec?: string
  typeGrade?: string
  pipeManufacturer?: string
  heatNumber?: string
}> {
  const byKey = new Map<string, PqrEditableMaterialRow>()
  initial.forEach((r) => byKey.set(`${r.lotIndex}:${r.pipeIndex}`, r))

  const out: Array<{
    lotIndex: number
    pipeIndex: number
    materialSpec?: string
    typeGrade?: string
    pipeManufacturer?: string
    heatNumber?: string
  }> = []

  current.forEach((cur) => {
    const prev = byKey.get(`${cur.lotIndex}:${cur.pipeIndex}`)
    if (!prev) return
    const entry: {
      lotIndex: number
      pipeIndex: number
      materialSpec?: string
      typeGrade?: string
      pipeManufacturer?: string
      heatNumber?: string
    } = { lotIndex: cur.lotIndex, pipeIndex: cur.pipeIndex }
    let touched = false
    ;(Object.keys(MATERIAL_FIELD_LABELS) as Array<keyof typeof MATERIAL_FIELD_LABELS>).forEach(
      (field) => {
        if (stringDiffers(prev[field], cur[field])) {
          entry[field] = (cur[field] ?? '').trim()
          touched = true
        }
      }
    )
    if (touched) out.push(entry)
  })
  return out
}
